import { motion } from 'framer-motion';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { RunSearchTable } from './RunSearchTable';
import { RunSearchFilters, type RunSearchFiltersState } from './RunSearchFilters';
import { searchRuns } from '@/api/runs/runs.api';
import type { RunResult, SearchRunsRequest, RunSortBy, SortOrder } from '@/api/runs/runs.types';
import { AlertCircle } from 'lucide-react';
import { Pagination } from "@/components/ui/pagination";
import { useSearchParams } from 'react-router-dom';

const DEFAULT_PAGE_SIZE = 5;

export function RunSearchList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [runs, setRuns] = useState<RunResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    
    // Initialize pagination from URL params
    const [currentPage, setCurrentPage] = useState(() => {
        const page = searchParams.get('page');
        return page ? parseInt(page) : 1;
    });
    const [pageSize, setPageSize] = useState(() => {
        const size = searchParams.get('size');
        return size ? parseInt(size) : DEFAULT_PAGE_SIZE;
    });
    
    // Initialize filters from URL params
    const [filters, setFilters] = useState<RunSearchFiltersState>(() => {
        const fromDate = searchParams.get('from');
        const toDate = searchParams.get('to');
        const statusParam = searchParams.get('status');
        
        return {
            query: searchParams.get('query') || '',
            searchMode: (searchParams.get('searchMode') as 'phrase' | 'match') || 'phrase',
            status: statusParam ? statusParam.split(',').filter(Boolean) as any[] : [],
            flowId: searchParams.get('flowId') || '',
            hasFailedSteps: searchParams.get('hasFailedSteps') === 'true' 
                ? true 
                : searchParams.get('hasFailedSteps') === 'false' 
                    ? false 
                    : undefined,
            dateRange: {
                from: fromDate ? new Date(fromDate) : undefined,
                to: toDate ? new Date(toDate) : undefined
            },
            minDurationMs: searchParams.get('minDuration') ? parseInt(searchParams.get('minDuration')!) : undefined,
            maxDurationMs: searchParams.get('maxDuration') ? parseInt(searchParams.get('maxDuration')!) : undefined,
            sortBy: (searchParams.get('sortBy') as 'start_time' | 'finish_time' | 'duration_ms') || 'start_time',
            sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc'
        };
    });
    
    const { error } = useEnhancedToast();
    
    // Use refs to track if we should auto-search
    const shouldAutoSearch = useRef(false);
    const isInitialMount = useRef(true);
    const hasFetchedInitial = useRef(false);

    const handleError = useCallback((err: unknown) => {
        error({
            icon: AlertCircle,
            title: "Failed to Search Runs",
            description: err instanceof Error ? err.message : "Could not search runs. Please try again.",
            duration: 4000,
        });
    }, [error]);

    const fetchRuns = useCallback(async (resetPage: boolean = false) => {
        setIsLoading(true);
        
        const pageToUse = resetPage ? 1 : currentPage;
        if (resetPage) {
            setCurrentPage(1);
        }
        
        try {
            // Build request params
            const requestParams: SearchRunsRequest = {
                page: pageToUse,
                pageSize: pageSize,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
                searchMode: filters.searchMode,
            };
            
            // Add optional filters
            if (filters.query) {
                requestParams.query = filters.query;
            }
            if (filters.flowId) {
                requestParams.flowId = filters.flowId;
            }
            if (filters.status.length > 0) {
                requestParams.status = filters.status;
            }
            if (filters.hasFailedSteps !== undefined) {
                requestParams.hasFailedSteps = filters.hasFailedSteps;
            }
            if (filters.dateRange.from) {
                const startDate = new Date(filters.dateRange.from);
                startDate.setHours(0, 0, 0, 0);
                requestParams.startDate = startDate.toISOString();
            }
            if (filters.dateRange.to) {
                const endDate = new Date(filters.dateRange.to);
                endDate.setHours(23, 59, 59, 999);
                requestParams.endDate = endDate.toISOString();
            }
            if (filters.minDurationMs) {
                requestParams.minDurationMs = filters.minDurationMs;
            }
            if (filters.maxDurationMs) {
                requestParams.maxDurationMs = filters.maxDurationMs;
            }
            
            const response = await searchRuns(requestParams);
            
            if (response.error) {
                handleError(new Error(response.error));
                setRuns([]);
                setTotalItems(0);
                setTotalPages(0);
            } else if (response.data) {
                setRuns(response.data.results);
                setTotalItems(response.data.total);
                setTotalPages(response.data.totalPages);
            }
        } catch (err) {
            handleError(err);
            setRuns([]);
            setTotalItems(0);
            setTotalPages(0);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize, filters, handleError]);

    // Initial load - fetch runs on mount
    useEffect(() => {
        if (isInitialMount.current && !hasFetchedInitial.current) {
            isInitialMount.current = false;
            hasFetchedInitial.current = true;
            fetchRuns();
        }
    }, [fetchRuns]);

    // Handle pagination changes - always fetch (but not on initial mount)
    useEffect(() => {
        if (!isInitialMount.current && hasFetchedInitial.current) {
            fetchRuns();
        }
    }, [currentPage, pageSize]);

    // Handle auto-search for certain filter changes (status, date, sort, hasFailedSteps)
    useEffect(() => {
        if (shouldAutoSearch.current && hasFetchedInitial.current) {
            shouldAutoSearch.current = false;
            fetchRuns(true);
        }
    }, [filters.status, filters.dateRange, filters.sortBy, filters.sortOrder, filters.hasFailedSteps, filters.searchMode]);

    // Update URL params when filters or pagination changes
    useEffect(() => {
        const params = new URLSearchParams();
        params.set('page', currentPage.toString());
        params.set('size', pageSize.toString());
        
        // Add filter params to URL
        if (filters.query) params.set('query', filters.query);
        if (filters.searchMode !== 'phrase') params.set('searchMode', filters.searchMode);
        if (filters.status.length > 0) params.set('status', filters.status.join(','));
        if (filters.flowId) params.set('flowId', filters.flowId);
        if (filters.hasFailedSteps !== undefined) params.set('hasFailedSteps', filters.hasFailedSteps.toString());
        if (filters.dateRange.from) params.set('from', filters.dateRange.from.toISOString());
        if (filters.dateRange.to) params.set('to', filters.dateRange.to.toISOString());
        if (filters.minDurationMs) params.set('minDuration', filters.minDurationMs.toString());
        if (filters.maxDurationMs) params.set('maxDuration', filters.maxDurationMs.toString());
        if (filters.sortBy !== 'start_time') params.set('sortBy', filters.sortBy);
        if (filters.sortOrder !== 'desc') params.set('sortOrder', filters.sortOrder);
        
        setSearchParams(params, { replace: true });
    }, [currentPage, pageSize, filters, setSearchParams]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleFiltersChange = (newFilters: RunSearchFiltersState) => {
        // Check if auto-searchable fields changed
        const autoSearchFields = ['status', 'dateRange', 'sortBy', 'sortOrder', 'hasFailedSteps', 'searchMode'];
        const changedAutoSearchField = autoSearchFields.some(field => {
            if (field === 'status') {
                return JSON.stringify(filters.status) !== JSON.stringify(newFilters.status);
            }
            if (field === 'dateRange') {
                return JSON.stringify(filters.dateRange) !== JSON.stringify(newFilters.dateRange);
            }
            return filters[field as keyof RunSearchFiltersState] !== newFilters[field as keyof RunSearchFiltersState];
        });
        
        if (changedAutoSearchField) {
            shouldAutoSearch.current = true;
        }
        
        setFilters(newFilters);
    };

    const handleSearch = () => {
        fetchRuns(true);
    };

    const handleReset = (resetFilters: RunSearchFiltersState) => {
        // Use the reset filters directly to avoid stale state
        setFilters(resetFilters);
        setCurrentPage(1);
        
        // Fetch with reset filters immediately
        const fetchWithResetFilters = async () => {
            setIsLoading(true);
            try {
                const requestParams: SearchRunsRequest = {
                    page: 1,
                    pageSize: pageSize,
                    sortBy: resetFilters.sortBy,
                    sortOrder: resetFilters.sortOrder,
                    searchMode: resetFilters.searchMode,
                };
                
                const response = await searchRuns(requestParams);
                
                if (response.error) {
                    handleError(new Error(response.error));
                    setRuns([]);
                    setTotalItems(0);
                    setTotalPages(0);
                } else if (response.data) {
                    setRuns(response.data.results);
                    setTotalItems(response.data.total);
                    setTotalPages(response.data.totalPages);
                }
            } catch (err) {
                handleError(err);
                setRuns([]);
                setTotalItems(0);
                setTotalPages(0);
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchWithResetFilters();
    };

    const handleSortChange = (sortBy: RunSortBy, sortOrder: SortOrder) => {
        const newFilters = { ...filters, sortBy, sortOrder };
        setFilters(newFilters);
        shouldAutoSearch.current = true;
    };

    return (
        <div className="flex-1 overflow-auto">
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="px-4 py-4 md:px-6 space-y-4"
            >
                {/* Filters Panel */}
                <RunSearchFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onSearch={handleSearch}
                    onReset={handleReset}
                    isLoading={isLoading}
                />

                {/* Results Section */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="space-y-3"
                >
                    <RunSearchTable
                        runs={runs}
                        isLoading={isLoading}
                        sortBy={filters.sortBy}
                        sortOrder={filters.sortOrder}
                        onSortChange={handleSortChange}
                    />

                    {totalItems > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalItems={totalItems}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    )}
                </motion.div>
            </motion.div>
        </div>
    );
}
